'use client'

import { useState } from 'react'
import Image from 'next/image'
import type { LedgerStreamEntry, LedgerTxEntry } from '@/lib/ledger-mapping'
import type { TxStatus } from '@/lib/ledger'
import { txStatusLabel, formatLedgerDate } from '@/lib/ledger'
import { STREAM_CATEGORIES } from '@/lib/stream'
import { Card } from './ui/Card'
import { Badge } from './ui/Badge'
import { StatusDot } from './ui/StatusDot'
import { ExternalLinkIcon } from './icons/ExternalLinkIcon'
import { ShareIcon } from './icons/ShareIcon'
import { ChevronIcon } from './icons/ChevronIcon'

interface Props {
  entry: LedgerStreamEntry
}

function formatDuration(secs: number | null): string {
  if (!secs) return ''
  const h = Math.floor(secs / 3600)
  const m = Math.floor((secs % 3600) / 60)
  const s = Math.round(secs % 60)
  return h > 0
    ? `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
    : `${m}:${String(s).padStart(2, '0')}`
}

function categoryLabel(value: string): string {
  return STREAM_CATEGORIES.find((c) => c.value === value)?.label ?? value
}

export default function LedgerEntryCard({ entry }: Props) {
  const [titleExpanded, setTitleExpanded] = useState(false)
  const txs: LedgerTxEntry[] = [entry.txStreamStart, entry.txStreamEnd, entry.txMp4Hash]
  const duration = formatDuration(entry.duration)

  function handleShare() {
    const url = `${window.location.origin}/ledger?stream=${entry.id}`
    if (navigator.share) {
      navigator.share({ title: entry.title, url }).catch(() => {})
    } else {
      navigator.clipboard.writeText(url)
    }
  }

  return (
    <Card
      hoverable
      data-component="LedgerEntryCard"
      data-entry-id={entry.id}
    >
      {/* ── Card Header ───────────────────────────────────────────────────── */}
      <div data-part="card-header" className="px-5 pt-5 pb-4">
        {/* Top row: category + share + duration */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            {entry.category && (
              <Badge>{categoryLabel(entry.category).toUpperCase()}</Badge>
            )}
            {duration && (
              <span className="font-mono text-[13px] text-mist dark:text-cream/30">
                {duration}
              </span>
            )}
          </div>
          <button
            onClick={handleShare}
            aria-label="Share this verification"
            className="shrink-0 p-1.5 rounded-lg text-mist dark:text-cream/30
              hover:text-carolina-dark dark:hover:text-carolina hover:bg-carolina/10
              transition-colors"
          >
            <ShareIcon />
          </button>
        </div>

        {/* Title */}
        <button
          type="button"
          aria-expanded={titleExpanded}
          aria-label={titleExpanded ? 'Collapse title' : 'Expand full title'}
          onClick={() => setTitleExpanded(!titleExpanded)}
          className="text-left w-full"
        >
          <h2
            className={`font-bold text-[18px] sm:text-[20px] text-ink dark:text-cream leading-tight mb-2 ${
              titleExpanded ? '' : 'line-clamp-1'
            }`}
          >
            {entry.title}
          </h2>
        </button>

        {/* Date */}
        <p className="font-mono text-[13px] text-mist dark:text-cream/30 mb-3">
          {formatLedgerDate(entry.streamedAt)}
        </p>

        {/* User row */}
        <div className="flex items-center gap-2.5 mb-3">
          {entry.avatarUrl ? (
            <Image
              src={entry.avatarUrl}
              alt=""
              width={24}
              height={24}
              className="w-6 h-6 rounded-full object-cover"
            />
          ) : (
            <span className="w-6 h-6 rounded-full bg-carolina/20 dark:bg-carolina/10" />
          )}
          <span className="font-mono text-[13px] tracking-[1px] text-carolina-dark/60 dark:text-carolina/60">
            {entry.username ? `@${entry.username}` : 'Anonymous'}
          </span>
        </div>

        {/* Original URL */}
        {entry.originalUrl && (
          <a
            href={entry.originalUrl}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="View original stream"
            className="inline-flex items-center gap-1.5 font-mono text-[13px] tracking-[1px]
              text-carolina-dark/70 dark:text-carolina/70 hover:text-carolina-dark dark:hover:text-carolina
              hover:underline underline-offset-2 transition-colors"
          >
            <ExternalLinkIcon /> Original stream
          </a>
        )}
      </div>

      {/* ── Transaction Rows ───────────────────────────────────────────────── */}
      <div
        data-part="tx-list"
        className="border-t border-carolina/[0.08] dark:border-carolina/[0.06] divide-y divide-carolina/[0.06] dark:divide-carolina/[0.04]"
      >
        {txs.map((tx, i) => (
          <TxRow key={`stream-${i}`} tx={tx} step={i + 1} />
        ))}
        {entry.clipTxs.map((tx, i) => (
          // Offset clip steps past the stream rows so data-step stays unique
          // within this tx-list. Visible badge is overridden by stepLabel="C".
          <TxRow
            key={`clip-${i}`}
            tx={tx}
            step={txs.length + i + 1}
            stepLabel="C"
          />
        ))}
      </div>

      {/* ── On-Chain Data (collapsible) ──────────────────────────────────── */}
      <OnChainFields entry={entry} />
    </Card>
  )
}

// ─── TX Row ─────────────────────────────────────────────────────────────────

function TxRow({
  tx,
  step,
  stepLabel,
}: {
  tx: LedgerTxEntry
  step: number
  /** Overrides the numeric step badge content (e.g. "C" for clip rows). */
  stepLabel?: string
}) {
  const { status } = tx
  const isActive = status === 'started' || status === 'processing'
  const isDone = status === 'completed'
  const isFailed = status === 'failed'
  const isPending = status === 'not-started'

  return (
    <div
      data-part="tx-row"
      data-status={status}
      className={`flex items-start gap-3 px-5 py-3.5 transition-colors ${
        isFailed ? 'bg-red-500/[0.04] dark:bg-red-500/[0.06]' : ''
      }`}
    >
      {/* Step badge */}
      <span
        className={`shrink-0 w-6 h-6 rounded-full flex items-center justify-center font-mono text-[12px] font-bold mt-0.5 border transition-colors ${
          isDone
            ? 'bg-green-400/15 border-green-400/40 text-green-400'
            : isFailed
              ? 'bg-red-400/15 border-red-400/40 text-red-400'
              : isActive
                ? 'bg-carolina/15 border-carolina-dark/40 dark:border-carolina/40 text-carolina-dark dark:text-carolina'
                : 'bg-ink/5 dark:bg-cream/5 border-ink/15 dark:border-cream/10 text-mist dark:text-cream/25'
        }`}
      >
        {stepLabel ?? step}
      </span>

      {/* Label + status */}
      <div className="flex-1 min-w-0">
        <p
          className={`font-mono text-[13px] tracking-[1px] mb-0.5 ${
            isPending ? 'text-mist dark:text-cream/25' : 'text-ink/80 dark:text-cream/80'
          }`}
        >
          {tx.label}
        </p>

        <StatusDot status={txDotStatus(status)} label={txStatusLabel(status)} />

        {/* Hash shown when completed */}
        {isDone && tx.txHash && (
          <p className="font-mono text-[12px] text-mist dark:text-cream/25 mt-0.5 truncate">
            {tx.txHash.slice(0, 10)}&hellip;{tx.txHash.slice(-8)}
          </p>
        )}

        {/* Failure detail */}
        {isFailed && (
          <p className="font-grotesk text-[14px] text-red-400/80 mt-1 leading-snug">
            Transaction issue &mdash; please wait for updates.
          </p>
        )}
      </div>

      {/* View on chain link — only when completed */}
      {isDone && tx.explorerUrl && (
        <a
          href={tx.explorerUrl}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={`View ${tx.label} on block explorer`}
          className="shrink-0 flex items-center gap-1.5 px-2.5 py-1 rounded border border-carolina-dark/20 dark:border-carolina/20
            text-carolina-dark/70 dark:text-carolina/70 hover:border-carolina-dark dark:hover:border-carolina
            hover:text-carolina-dark dark:hover:text-carolina font-mono text-[12px] tracking-[1px] transition-all mt-0.5"
        >
          VIEW <ExternalLinkIcon />
        </a>
      )}
    </div>
  )
}

// ─── On-Chain Fields (collapsible) ──────────────────────────────────────────

function OnChainFields({ entry }: { entry: LedgerStreamEntry }) {
  const [open, setOpen] = useState(false)
  const hidden = new Set(entry.ledgerVisibility ?? [])

  const fields: { key: string; label: string; value: string | null }[] = [
    { key: 'chainStreamId', label: 'CHAIN STREAM ID', value: entry.chainStreamId },
    { key: 'broadcastId', label: 'BROADCAST ID', value: entry.broadcastId },
    { key: 'liveStreamId', label: 'LIVE STREAM ID', value: entry.liveStreamId },
    { key: 'initialSignature', label: 'INITIAL SIGNATURE', value: entry.initialSignature },
    { key: 'finalSignature', label: 'FINAL SIGNATURE', value: entry.finalSignature },
    { key: 'chainContractAddress', label: 'CONTRACT', value: entry.chainContractAddress },
  ]

  const visible = fields.filter((f) => f.value && !hidden.has(f.key))
  if (visible.length === 0) return null

  return (
    <div
      data-part="on-chain-fields"
      className="border-t border-carolina/[0.08] dark:border-carolina/[0.06]"
    >
      <button
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        aria-label={open ? 'Hide on-chain data' : 'Show on-chain data'}
        className="w-full flex items-center justify-between px-5 py-3 text-left group"
      >
        <span className="font-mono text-[12px] tracking-[2px] text-steel dark:text-cream/30">
          ON-CHAIN DATA
        </span>
        <ChevronIcon
          className={`text-steel dark:text-cream/30 transition-transform duration-200 ${
            open ? 'rotate-180' : ''
          }`}
        />
      </button>

      {open && (
        <div className="px-5 pb-4 space-y-2">
          {visible.map((f) => {
            const val = f.value ?? ''
            const isLong = val.length > 20
            const display = isLong
              ? `${val.slice(0, 10)}...${val.slice(-8)}`
              : val

            const isContract = f.key === 'chainContractAddress' && entry.chainExplorerUrl

            return (
              <div key={f.key} className="flex items-baseline gap-3">
                <span className="font-mono text-[11px] tracking-[1.5px] text-mist dark:text-cream/25 shrink-0 w-32">
                  {f.label}
                </span>
                {isContract ? (
                  <a
                    href={`${entry.chainExplorerUrl}/address/${val}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-mono text-[13px] text-carolina-dark dark:text-carolina hover:underline underline-offset-2 truncate"
                  >
                    {display}
                  </a>
                ) : (
                  <span className="font-mono text-[13px] text-ink/60 dark:text-cream/50 truncate">
                    {display}
                  </span>
                )}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

// ─── TX Status Mapping ──────────────────────────────────────────────────────

function txDotStatus(status: TxStatus) {
  switch (status) {
    case 'not-started': return 'idle' as const
    case 'started':     return 'warning' as const
    case 'processing':  return 'active' as const
    case 'completed':   return 'success' as const
    case 'failed':      return 'error' as const
  }
}

